import { useEffect } from 'react';
import { useForm, useWatch, Control } from 'react-hook-form';
import { isCleanText } from '@/utils/sanitize';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Select, SelectOption } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import CopyableInputField from '@/components/ui/CopyableInputField';

// With yupResolver the Input/Textarea clean-text rule is skipped, so the schema keeps the check
const schema = yup.object().shape({
  parent_category_id: yup.string().required('Parent category is required'),
  category_name: yup.string().required('Category name is required')
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v)),
  category_description: yup.string()
    .test('clean-text', 'Please use only letters, numbers and standard punctuation', (v) => isCleanText(v)),
  is_active: yup.boolean(),
});

type CategoryFormValues = yup.InferType<typeof schema>;

interface CategoryFormProps {
  onSubmit: (data: any) => void;
  parentCategories?: any[];
  allCategories?: any[];
  category?: any;
  isSubmitting?: boolean;
  onCancel: () => void;
   apiError?: { field: string; message: string } | null;
}

export default function CategoryForm({
  onSubmit,
  parentCategories = [],
  allCategories = [],
  category,
  isSubmitting = false,
  onCancel,
   apiError,
}: CategoryFormProps) {
  const isEditMode = !!category;

  const {
    handleSubmit,
    formState: { errors },
    reset,
    control,
    setValue,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
  });
  // yupResolver gives control a separate transformed-values type that the shared fields don't accept
  const formControl = control as unknown as Control<CategoryFormValues>;
 useEffect(() => {
    if (apiError?.field && apiError?.message) {
      setError(apiError.field as any, {
        type: 'server',
        message: apiError.message,
      });
    }
  }, [apiError, setError]);
  // Format parent categories for react-select
  const parentCategoryOptions: SelectOption[] = [
    { label: 'None (Root Category)', value: 'root' },
    ...(parentCategories?.map(cat => ({
      label: cat.category_name,
      value: cat._id,
    })) || []),
  ];

  useEffect(() => {
    if (isEditMode && category) {
      reset({
        parent_category_id: category.parent_category_id || 'root',
        category_name: category.category_name,
        category_description: category.category_description || '',
        is_active: category.is_active,
      });
    } else {
      reset({
        parent_category_id: 'root',
        category_name: '',
        category_description: '',
        is_active: true,
      });
    }
  }, [isEditMode, category, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full overflow-hidden">
      <div className="p-4 sm:p-6 grid grid-cols-1 gap-4 sm:gap-6 overflow-y-auto flex-1">
        <div>
          <Select
            name="parent_category_id"
            control={formControl}
            label="Parent Category"
            options={parentCategoryOptions}
            placeholder="Select parent category..."
            isClearable={false}
            isSearchable={true}
            error={errors.parent_category_id?.message}
            required
          />
        </div>

        <Input
          name="category_name"
          control={formControl}
          label="Category Name"
          required
          type="text"
          placeholder="Enter category name..."
        />

        {isEditMode && category?.category_slug && (
          <CopyableInputField
            id="slug-display"
            label="Slug"
            value={category.category_slug}
            helperText="URL-friendly version of the category name"
          />
        )}

        <Textarea
          name="category_description"
          control={formControl}
          label="Description"
          rows={4}
          placeholder="Enter category description (optional)..."
        />
      </div>

      <div className="p-4 sm:p-6 bg-gray-50 border-t flex flex-col sm:flex-row justify-end gap-2 sm:gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary w-full sm:w-auto order-2 sm:order-1 text-sm sm:text-base py-2"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary disabled:opacity-50 w-full sm:w-auto order-1 sm:order-2 text-sm sm:text-base py-2"
          disabled={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Category' : 'Create Category')}
        </button>
      </div>
    </form>
  );
}
